/**
 * KPI dashboard read contract (KPI workflow MVP, v1).
 *
 * Source of truth: the KPI workflow epic ledger, §"Schema + API contract"
 * (v1.1) — `GET /api/kpi/table` and
 * `POST /api/kpi/values/{id}/override`, served by T1's router.
 *
 * The epic pins the ENDPOINTS and the underlying columns but not the JSON
 * envelope of `/api/kpi/table`. The shapes below are T4's proposal for that
 * envelope, derived one-for-one from `kpi_metric_definitions` /
 * `kpi_metric_values`; two additions beyond a literal column dump are called
 * out where they are declared (`properties`, `effective_value`). Anything the
 * server changes here is a contract change and goes through the orchestrator.
 *
 * Parsed with Zod at the boundary (`zod/v3` import, matching `types/evidence.ts`
 * — the installed package is v4 and only the v3 entrypoint is used in this repo).
 */

import { z } from "zod/v3";

/** Unit of a metric — drives formatting, never math. */
export const KpiUnitSchema = z.enum(["usd", "count", "pct", "ratio"]);
export type KpiUnit = z.infer<typeof KpiUnitSchema>;

/**
 * How a metric rolls up across properties. Present so the portfolio tab can
 * SAY how a number was reached; the arithmetic itself is the server's.
 */
export const KpiAggregationSchema = z.enum([
	"sum",
	"unit_weighted",
	"recompute",
	"none",
]);
export type KpiAggregation = z.infer<typeof KpiAggregationSchema>;

/** Extraction confidence: 'verified' = the server re-read the cited cell. */
export const KpiConfidenceSchema = z.enum(["verified", "unverified"]);
export type KpiConfidence = z.infer<typeof KpiConfidenceSchema>;

/**
 * Row lifecycle. `/api/kpi/table` returns active rows only, so 'superseded'
 * never reaches the grid — it is in the union because the column carries it
 * and a stray value must not fail the parse.
 */
export const KpiValueStatusSchema = z.enum([
	"active",
	"superseded",
	"overridden",
]);
export type KpiValueStatus = z.infer<typeof KpiValueStatusSchema>;

/**
 * Where a number came from. Excel cells carry sheet+ref, PDFs carry page;
 * exactly which are populated depends on the source format, so all but the
 * file are nullable.
 */
export const KpiSourceRefSchema = z.object({
	file: z.string(),
	sheet: z.string().nullable().optional(),
	ref: z.string().nullable().optional(),
	page: z.number().int().nullable().optional(),
	/**
	 * v1.5. Identity of the FILE the value was read from — the same id the files
	 * API and the `doppler://` viewers address documents by. It is what turns a
	 * citation from a sentence into a link: a filename cannot be resolved to
	 * bytes, since names repeat across months and properties. Null on manual,
	 * backfilled, or pre-v1.5 rows, which stay text-only.
	 */
	attachment_id: z.number().int().nullable().optional(),
	/**
	 * v1.5. Digest of the bytes that were read. Carried so a citation can later
	 * be shown as STALE when the document behind it changed; nothing reads it
	 * yet, and it is declared so the parser does not strip it.
	 */
	content_sha256: z.string().nullable().optional(),
});
export type KpiSourceRef = z.infer<typeof KpiSourceRefSchema>;

/**
 * One property's contribution to a rollup cell. Portfolio numbers are the
 * whole point of the rollup tab and also the easiest to distrust, so the cell
 * carries its parts.
 */
export const KpiContributorSchema = z.object({
	property_key: z.string(),
	value: z.number().nullable(),
});
export type KpiContributor = z.infer<typeof KpiContributorSchema>;

/**
 * One value a computed cell was calculated FROM (contract v1.3, extended by
 * v1.4 with `unit` and `property_key`).
 *
 * This is what makes a derived number auditable: without it the drill-in can
 * only say "calculated", which asks the user to trust arithmetic they cannot
 * see over sources they can. Each input carries its own citation and its own
 * confidence, because a computed number is only as checkable as its weakest
 * input — and `overridden` marks the ones a human replaced, which is exactly
 * the case where a total stops matching the documents behind it.
 *
 * `value` is the EFFECTIVE value (override included), so the listed inputs
 * reproduce the result.
 *
 * Every field but `metric_key` is nullable: an input can be missing a citation
 * (itself computed) without the cell — or the whole table — failing to parse.
 */
export const KpiCellInputSchema = z.object({
	metric_key: z.string(),
	/**
	 * v1.7. Public id of this stored row — what the override endpoint takes.
	 * It is how a computed cell whose provenance is a SINGLE row can offer that
	 * row's override instead of refusing on the grounds that arithmetic cannot
	 * be corrected. Null where the input is itself computed.
	 */
	value_id: z.string().nullable().optional(),
	/**
	 * How the row participated. T1's vocabulary: 'input' (a formula input),
	 * 'component' (a piece a fill-gap value was assembled from), 'contributor'
	 * (one property's value feeding a rollup). Left a free string — the display
	 * shows it verbatim, and a new role must not fail the parse.
	 */
	role: z.string().nullable().optional(),
	value: z.number().nullable().optional(),
	/**
	 * v1.8. The value extraction found on the stored row, BEFORE any override —
	 * the number a revert restores. `value` above is the EFFECTIVE value
	 * (override included), so on an overridden input this is the only place the
	 * original number is visible; the revert control needs it to say what it
	 * restores. Null on computed inputs and pre-v1.8 servers.
	 */
	extracted_value: z.number().nullable().optional(),
	/**
	 * v1.4. The INPUT's own unit, which is frequently not the computed cell's:
	 * a vacancy rate is a `pct` built from `count`s, and formatting its inputs
	 * with the cell's unit renders 100 units as "10,000.00%". Absent on a
	 * pre-v1.4 server, in which case the value is shown unitless rather than
	 * dressed in a unit it may not have.
	 */
	unit: KpiUnitSchema.nullable().optional(),
	/**
	 * v1.4. Populated on rollup inputs: without it, three `net_operating_income`
	 * rows under one portfolio cell are indistinguishable, which defeats the
	 * point of listing them.
	 */
	property_key: z.string().nullable().optional(),
	source: KpiSourceRefSchema.nullable().optional(),
	confidence: KpiConfidenceSchema.nullable().optional(),
	overridden: z.boolean().nullable().optional(),
});
export type KpiCellInput = z.infer<typeof KpiCellInputSchema>;

/**
 * One cell of the grid: a metric at a period.
 *
 * `value` is what extraction found; `effective_value` is what the user should
 * see — the override when one exists, otherwise `value`. The server computes
 * it so that the UI never has to decide which number is authoritative (and so
 * derived/aggregated cells, which have no single stored row, still have one).
 */
export const KpiCellSchema = z.object({
	/**
	 * PUBLIC id (UUID string) of the backing `kpi_metric_values` row — the id
	 * `POST /api/kpi/values/{public_id}/override` takes. The internal integer PK
	 * is never exposed (backend/CLAUDE.md). NULL for derived and rollup cells:
	 * they are computed from other rows, so there is nothing to override.
	 */
	value_id: z.string().nullable(),
	value: z.number().nullable(),
	effective_value: z.number().nullable(),
	unit: KpiUnitSchema,
	confidence: KpiConfidenceSchema,
	status: KpiValueStatusSchema,
	source: KpiSourceRefSchema.nullable().optional(),
	override_value: z.number().nullable().optional(),
	override_by: z.string().nullable().optional(),
	/** Epoch ms (backend JSON rule), null for computed cells. */
	extracted_at: z.number().nullable().optional(),
	/** True when the number was computed in Python rather than extracted. */
	derived: z.boolean().optional(),
	/**
	 * Rollup cells only. NULLABLE, not merely optional: the server declares the
	 * field on every cell and sends an explicit `null` on the ones that have no
	 * contributors, so a property grid fails to parse against `.optional()`
	 * alone (verified against T1's real envelope in `kpiContract.test.ts`).
	 */
	contributors: z.array(KpiContributorSchema).nullable().optional(),
	/**
	 * Computed cells only (contract v1.3). Nullable for the same reason
	 * `contributors` is: the server declares the field on every cell.
	 */
	inputs: z.array(KpiCellInputSchema).nullable().optional(),
});
export type KpiCell = z.infer<typeof KpiCellSchema>;

/**
 * Why a metric row holds no number anywhere on this grid (DOP-293).
 *
 * `kind` is a stable token, deliberately not a sentence: the wording is the
 * UI's (`components/Kpi/kpiEmptyRow.ts`), and the server's internal planner
 * vocabulary — "near_miss", "no_source" — never reaches a screen. Left a plain
 * string rather than an enum so a kind this build does not know falls back to
 * the generic line instead of failing the whole table's parse.
 *
 * `detail` is the run planner's own clause, when it wrote one. `missing_inputs`
 * carries the DISPLAY names of a calculated row's absent inputs.
 */
export const KpiEmptyReasonSchema = z.object({
	kind: z.string(),
	detail: z.string().nullable().optional(),
	missing_inputs: z.array(z.string()).default([]),
});
export type KpiEmptyReason = z.infer<typeof KpiEmptyReasonSchema>;

/** One metric row. `cells` is keyed by period date (`YYYY-MM-DD`). */
export const KpiMetricRowSchema = z.object({
	key: z.string(),
	label: z.string(),
	unit: KpiUnitSchema,
	aggregation: KpiAggregationSchema,
	is_raw: z.boolean(),
	/**
	 * v1.6. The equation this metric was computed by, as a reader sees it —
	 * "DSCR = Net Operating Income / Debt Service". The server sources it from
	 * the formula object itself, so it cannot describe arithmetic the code does
	 * not perform.
	 *
	 * Null on raw metrics (read off a document, not computed) and on the
	 * portfolio rollup (an aggregation of already-computed values, not the
	 * property formula).
	 */
	formula_display: z.string().nullable().optional(),
	/**
	 * v1.9 (DOP-293). Present only on a row this grid renders with no number in
	 * any column, and absent on a pre-v1.9 server — which is why the empty-row
	 * filter treats a missing reason as "no value found" rather than as a row it
	 * cannot show.
	 */
	empty_reason: KpiEmptyReasonSchema.nullable().optional(),
	cells: z.record(z.string(), KpiCellSchema),
});
export type KpiMetricRow = z.infer<typeof KpiMetricRowSchema>;

/** A metric group, in the order the metric pack defines it. */
export const KpiMetricGroupSchema = z.object({
	key: z.string(),
	label: z.string(),
	metrics: z.array(KpiMetricRowSchema),
});
export type KpiMetricGroup = z.infer<typeof KpiMetricGroupSchema>;

/**
 * Period bucket the UI can REQUEST and label — the three the toggle offers.
 *
 * Deliberately NOT the type of `period_grain` on the wire: that stays a plain
 * string, and the value the server sends is carried through UNCHANGED. A
 * server that grows a bucket this build does not know (a week, a trailing
 * twelve) is serving honest data under a name this code cannot label, and
 * coercing that name to "month" would put false labels over real numbers —
 * "Oct 2023" above a week that starts on the 2nd. So an unknown grain is
 * recognised as unknown (`isSupportedPeriodGrain`), rendered as the raw period
 * key, and named to the user.
 */
export type KpiPeriodGrain = "month" | "quarter" | "year";

export const KPI_PERIOD_GRAINS: readonly KpiPeriodGrain[] = [
	"month",
	"quarter",
	"year",
];

/**
 * Can this build label periods in the grain the server sent?
 *
 * The one narrowing point. Everything else keeps the server's own string, so
 * an unsupported grain can still be reported by name.
 */
export function isSupportedPeriodGrain(
	value: string | null | undefined,
): value is KpiPeriodGrain {
	return value === "month" || value === "quarter" || value === "year";
}

export const KpiTableSchema = z.object({
	/** The property this grid is for; null on the portfolio rollup. */
	property_key: z.string().nullable(),
	/** 'portfolio' when this is the rollup, null for a single property. */
	rollup: z.literal("portfolio").nullable().optional(),
	period_grain: z.string().default("month"),
	/** Period dates (`YYYY-MM-DD`), ascending — the grid's columns. */
	periods: z.array(z.string()),
	groups: z.array(KpiMetricGroupSchema),
	/**
	 * ADDITION beyond the epic's column list: every property key the client has
	 * KPI data for. The contract has no properties endpoint, and the dashboard
	 * cannot render a property tab strip without one; returning it on the table
	 * response avoids adding a second round trip for a three-item list.
	 */
	properties: z.array(z.string()).default([]),
});
export type KpiTable = z.infer<typeof KpiTableSchema>;

/**
 * Response of `POST /api/kpi/values/{public_id}/override` and its DELETE
 * revert. `id` is the same public UUID the request addressed — the internal PK
 * stays server-side. On a revert `status` comes back `"active"` and the
 * override fields are null; one schema covers both because the envelope is
 * identical.
 */
export const KpiOverrideResponseSchema = z.object({
	id: z.string(),
	override_value: z.number().nullable(),
	status: KpiValueStatusSchema,
	override_by: z.string().nullable().optional(),
});
export type KpiOverrideResponse = z.infer<typeof KpiOverrideResponseSchema>;

/**
 * Parse a `/api/kpi/table` payload. Returns null on a shape mismatch rather
 * than throwing, so a bad payload becomes an error state instead of a blank
 * screen with an exception behind it.
 */
export function parseKpiTable(payload: unknown): KpiTable | null {
	const result = KpiTableSchema.safeParse(payload);
	return result.success ? result.data : null;
}

export function parseKpiOverrideResponse(
	payload: unknown,
): KpiOverrideResponse | null {
	const result = KpiOverrideResponseSchema.safeParse(payload);
	return result.success ? result.data : null;
}

/**
 * A user-defined calculated row (contract: `/api/kpi/custom-metrics`).
 *
 * The row itself arrives inside the normal table payload, in a final "custom"
 * group, indistinguishable from a packaged calc metric — which is the point:
 * the grid renders it with no special case. This shape exists only so the page
 * can MANAGE those rows (list them to map a grid row back to the id a delete
 * addresses, create one, remove one).
 */
export const KpiCustomMetricSchema = z.object({
	id: z.string(),
	key: z.string(),
	label: z.string(),
	expr: z.string(),
	unit: KpiUnitSchema,
});
export type KpiCustomMetric = z.infer<typeof KpiCustomMetricSchema>;

export const KpiCustomMetricListSchema = z.object({
	metrics: z.array(KpiCustomMetricSchema),
});

export function parseKpiCustomMetricList(
	payload: unknown,
): KpiCustomMetric[] | null {
	const result = KpiCustomMetricListSchema.safeParse(payload);
	return result.success ? result.data.metrics : null;
}

export function parseKpiCustomMetric(payload: unknown): KpiCustomMetric | null {
	const result = KpiCustomMetricSchema.safeParse(payload);
	return result.success ? result.data : null;
}

/** Group key the server files user-defined rows under. */
export const KPI_CUSTOM_GROUP_KEY = "custom";
/** Key prefix the server mints custom metrics with. */
export const KPI_CUSTOM_KEY_PREFIX = "custom_";

/**
 * Is this grid row user-defined — i.e. removable?
 *
 * Either signal is sufficient: the key prefix identifies the row wherever it is
 * rendered, and the group key covers a server that files a row under "custom"
 * without minting a prefixed key.
 */
export function isCustomMetricRow(
	metricKey: string,
	groupKey?: string,
): boolean {
	return (
		metricKey.startsWith(KPI_CUSTOM_KEY_PREFIX) ||
		groupKey === KPI_CUSTOM_GROUP_KEY
	);
}

/** The visual state of a cell — verified / unverified / overridden. */
export type KpiCellState = "verified" | "unverified" | "overridden" | "empty";

export function cellState(cell: KpiCell | undefined): KpiCellState {
	if (!cell || cell.effective_value === null) return "empty";
	if (cell.status === "overridden") return "overridden";
	return cell.confidence === "verified" ? "verified" : "unverified";
}
